import { SendIcon } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/shadcn/utils';
import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import { candidateJobQueryKeys } from '@/modules/candidates/queries/queryKeys';
import type { CandidateJobApi } from '@/modules/candidates/types';
import { useSendDraftMessagesMutation } from '@/modules/messaging/mutations/useSendDraftMessagesMutation';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { invalidateQuery } from '@/utils/queries';

type ReachOutMessageApi = NonNullable<CandidateJobApi['reachOutMessage']>;
type MessageStatusApi = ReachOutMessageApi['status'];

const messageStatusConfig: Record<
  MessageStatusApi,
  { label: string; description: string; className: string }
> = {
  Draft: {
    label: 'Draft',
    description: 'A reach-out message is in draft state.',
    className: 'border-orange-300 bg-orange-100 text-orange-800',
  },
  Pending: {
    label: 'Pending',
    description: 'Reach-out message is queued for sending.',
    className: 'border-orange-300 bg-orange-100 text-orange-800',
  },
  // should never be visible, because by then we switch to "contacted" state
  Sent: {
    label: 'Sent',
    description: 'Reach-out message has been sent to the candidate.',
    className: 'border-orange-300 bg-orange-100 text-orange-800',
  },
  // should never be visible, because by then we switch to "contacted" state
  Read: {
    label: 'Read',
    description: 'Candidate has read the reach-out message.',
    className: 'border-orange-300 bg-orange-100 text-orange-800',
  },
  Failed: {
    label: 'Failed',
    description: 'Message failed to send. Try sending it again.',
    className: 'border-red-300 bg-red-100 text-red-800',
  },
};

interface CandidateContactStatusProps {
  jobId: string;
  reachOutMessage: ReachOutMessageApi;
}

export function CandidateContactStatus({
  jobId,
  reachOutMessage,
}: CandidateContactStatusProps) {
  const config = messageStatusConfig[reachOutMessage.status];

  return (
    <div className="flex items-center gap-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <Badge size="md" className={cn('border', config.className)}>
              {config.label}
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent>{config.description}</TooltipContent>
      </Tooltip>
      {reachOutMessage.status === 'Failed' && (
        <PermissionsGate permissions={[permissions.Messaging.Write]}>
          <RetryFailedMessageButton
            jobId={jobId}
            messageId={reachOutMessage.id}
          />
        </PermissionsGate>
      )}
    </div>
  );
}

interface RetryFailedMessageButtonProps {
  jobId: string;
  messageId: string;
}

function RetryFailedMessageButton({
  jobId,
  messageId,
}: RetryFailedMessageButtonProps) {
  const sendMutation = useSendDraftMessagesMutation();

  const handleRetry = (event: React.MouseEvent) => {
    event.stopPropagation();
    sendMutation.mutate(
      { messageIds: [messageId] },
      {
        onSuccess: () => {
          toast.success('Message queued for sending.');
          invalidateQuery(candidateJobQueryKeys.lists(jobId))();
        },
        onError: (error) => {
          toast.error(`Could not send message: ${extractErrorMessage(error)}`);
        },
      },
    );
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          onClick={handleRetry}
          loading={sendMutation.isPending}
          aria-label="Send failed message"
        >
          <SendIcon className="size-3" />
          Send
        </Button>
      </TooltipTrigger>
      <TooltipContent>Retry sending</TooltipContent>
    </Tooltip>
  );
}
